import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import Chart from '../lib/chartTheme';
import { ApiError } from '../lib/api';
import {
  categoriseFailure,
  COLLECTED_MEASURES,
  defaultCustomRange,
  FAIL_CATEGORIES,
  FLOWS,
  fetchReport,
  fetchStops,
  formatDateAU,
  formatPeriodLabel,
  pct,
  periodKey,
  rangeToDates,
  type DateRange,
  type DifotReport,
  type DifotStop,
  type Filters,
  type Flow,
  type Period,
  type RegionOption,
} from '../lib/difot';
import '../difot.css';

const RANGE_LABELS: Record<DateRange, string> = {
  all: 'All available',
  today: 'Today',
  yesterday: 'Yesterday',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  custom: 'Custom range',
};

/** Switching to a custom window seeds the inputs from the window already on screen. */
function applyRange(filters: Filters, range: DateRange): Filters {
  if (range !== 'custom') return { ...filters, range };
  return { ...filters, range, ...defaultCustomRange(filters.range, filters) };
}

/** Header caption for the active window — custom ranges spell out the dates. */
function windowLabel(filters: Filters): string {
  if (filters.range !== 'custom') return RANGE_LABELS[filters.range];
  const { from, to } = rangeToDates('custom', filters);
  if (!from && !to) return 'Custom range';
  if (from && to) return from === to ? formatDateAU(from) : `${formatDateAU(from)} – ${formatDateAU(to)}`;
  return from ? `From ${formatDateAU(from)}` : `Up to ${formatDateAU(to)}`;
}

export function Difot() {
  const [filters, setFilters] = useState<Filters>({
    range: 'yesterday',
    flow: 'collect',
    regions: [],
    drivers: [],
    status: '',
  });
  const [period, setPeriod] = useState<Period>('daily');
  const [report, setReport] = useState<DifotReport | null>(null);
  const [failures, setFailures] = useState<DifotStop[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // A half-filled custom range would query an open-ended window, so hold off
  // until both ends are set rather than flashing an unintended report.
  const awaitingCustomDates = filters.range === 'custom' && (!filters.from || !filters.to);

  const load = useCallback(async () => {
    if (filters.range === 'custom' && (!filters.from || !filters.to)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Windows are anchored to the live business day, so a single windowed
      // fetch is enough — no all-time pre-fetch or latest-day anchor needed.
      const rep = await fetchReport(filters);
      // Render the report immediately — the failure list is secondary and must
      // not be able to blank the whole dashboard if it fails.
      setReport(rep);
    } catch (err) {
      console.error('DIFOT report load failed', err);
      setError(
        err instanceof ApiError
          ? `Could not load the DIFOT report: ${err.message} (${err.status})`
          : err instanceof Error
            ? `Could not load the DIFOT report: ${err.message}`
            : 'Failed to load the DIFOT report',
      );
      setLoading(false);
      return;
    }

    try {
      const { from, to } = rangeToDates(filters.range, filters);
      const fails = await fetchStops({
        from,
        to,
        flow: filters.flow,
        regions: filters.regions,
        drivers: filters.drivers,
        status: 'Failed',
        limit: 600,
      });
      setFailures(fails);
    } catch {
      setFailures([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="difot">
      <Header label={windowLabel(filters)} loading={loading} onRefresh={() => void load()} />

      <FlowTabs
        flow={filters.flow ?? 'collect'}
        // Regions and drivers belong to one flow, so a switch starts them afresh.
        onChange={(flow) => setFilters({ ...filters, flow, regions: [], drivers: [] })}
      />

      {error && (
        <p className="difot-error" role="alert">
          {error}
        </p>
      )}

      <FilterBar
        filters={filters}
        period={period}
        options={report?.options ?? { drivers: [], regions: [] }}
        onChange={setFilters}
        onPeriod={setPeriod}
      />

      {awaitingCustomDates && (
        <p className="difot-hint">Pick a start and end date to run the report over a custom window.</p>
      )}

      {report && (
        <>
          <Kpis report={report} />
          <section className="card difot-section">
            <div className="difot-section-head">
              <div>
                <div className="difot-section-title">DIFOT Trend</div>
                <div className="difot-section-sub">
                  Deliveries completed as a share of attempted stops, across the selected period
                </div>
              </div>
              <Legend
                items={[
                  { c: '#13a874', t: 'DIFOT %' },
                  { c: 'rgba(98,104,102,0.4)', t: 'Stops' },
                  { c: '#d9412f', t: 'Failed' },
                ]}
              />
            </div>
            <TrendChart report={report} period={period} />
          </section>

          <CollectedPanel report={report} period={period} flow={filters.flow ?? 'collect'} />

          <div className="difot-grid cols-3">
            <section className="card difot-section">
              <div className="difot-section-head">
                <div>
                  <div className="difot-section-title">Performance by Region</div>
                  <div className="difot-section-sub">Volume and DIFOT % by team region</div>
                </div>
              </div>
              <RegionChart report={report} />
              <RegionCards report={report} />
            </section>

            <FailurePanel failures={failures} />
          </div>

          <section className="card difot-section">
            <div className="difot-section-head">
              <div>
                <div className="difot-section-title">Driver / Run Leaderboard</div>
                <div className="difot-section-sub">Ranked by attempted deliveries</div>
              </div>
            </div>
            <DriverTable report={report} />
          </section>

          <StopLookup />
        </>
      )}

      {loading && !report && <p className="muted">Loading DIFOT report…</p>}
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

function Header({
  label,
  loading,
  onRefresh,
}: {
  label: string;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <header className="difot-head">
      <div className="difot-brand">
        <div>
          <div className="difot-eyebrow">WARRRL · Powered by Locate2u</div>
          <h1>DIFOT Performance Dashboard</h1>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="difot-head-meta">
          <div>
            Window <span className="strong">{label}</span>
          </div>
          <div>{loading ? 'Refreshing…' : 'Up to date'}</div>
        </div>
        <button type="button" className="difot-btn" onClick={onRefresh} disabled={loading}>
          Refresh
        </button>
      </div>
    </header>
  );
}

// ─── Flow tabs ────────────────────────────────────────────────────────────

function FlowTabs({ flow, onChange }: { flow: Flow; onChange: (flow: Flow) => void }) {
  return (
    <div className="difot-flows" role="tablist" aria-label="Operation">
      {FLOWS.map((f) => (
        <button
          key={f.key}
          type="button"
          role="tab"
          aria-selected={flow === f.key}
          className={flow === f.key ? 'difot-flow active' : 'difot-flow'}
          onClick={() => onChange(f.key)}
        >
          <span className="difot-flow-label">{f.label}</span>
          <span className="difot-flow-blurb">{f.blurb}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Filters ──────────────────────────────────────────────────────────────

function FilterBar({
  filters,
  period,
  options,
  onChange,
  onPeriod,
}: {
  filters: Filters;
  period: Period;
  options: { drivers: string[]; regions: RegionOption[] };
  onChange: (f: Filters) => void;
  onPeriod: (p: Period) => void;
}) {
  const regionOptions = useMemo(
    () =>
      options.regions
        .filter((r) => r.flow === filters.flow)
        .map((r) => ({ value: String(r.id), label: r.name, depth: r.depth })),
    [options.regions, filters.flow],
  );
  const driverOptions = useMemo(() => options.drivers.map((d) => ({ value: d, label: d })), [options.drivers]);

  return (
    <div className="card difot-filters">
      <div className="difot-field">
        <label>Trend period</label>
        <div className="difot-seg">
          {(['daily', 'weekly', 'monthly'] as Period[]).map((p) => (
            <button
              key={p}
              type="button"
              className={period === p ? 'active' : ''}
              onClick={() => onPeriod(p)}
            >
              {p[0].toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="difot-field">
        <label>Date range</label>
        <select value={filters.range} onChange={(e) => onChange(applyRange(filters, e.target.value as DateRange))}>
          <option value="all">All available</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last7">Last 7 days</option>
          <option value="last30">Last 30 days</option>
          <option value="custom">Custom range…</option>
        </select>
      </div>
      {filters.range === 'custom' && (
        <div className="difot-field difot-field-wide">
          <label>Custom dates</label>
          <div className="difot-dates">
            <input
              type="date"
              aria-label="Start date"
              value={filters.from ?? ''}
              max={filters.to || undefined}
              onChange={(e) => onChange({ ...filters, from: e.target.value })}
            />
            <span className="difot-dates-sep">to</span>
            <input
              type="date"
              aria-label="End date"
              value={filters.to ?? ''}
              min={filters.from || undefined}
              onChange={(e) => onChange({ ...filters, to: e.target.value })}
            />
          </div>
        </div>
      )}
      <div className="difot-field difot-field-wide">
        <label id="difot-region-label">Region</label>
        <MultiSelect
          labelledBy="difot-region-label"
          allLabel="All regions"
          noun="region"
          options={regionOptions}
          selected={filters.regions}
          onChange={(regions) => onChange({ ...filters, regions })}
        />
      </div>
      <div className="difot-field difot-field-wide">
        <label id="difot-driver-label">Driver / Run</label>
        <MultiSelect
          labelledBy="difot-driver-label"
          allLabel="All drivers"
          noun="driver"
          options={driverOptions}
          selected={filters.drivers}
          onChange={(drivers) => onChange({ ...filters, drivers })}
        />
      </div>
      <div className="difot-field">
        <label>Status</label>
        <select value={filters.status} onChange={(e) => onChange({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="Complete">Complete</option>
          <option value="Failed">Failed</option>
          <option value="Pending">Pending</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>
    </div>
  );
}

interface MultiOption {
  value: string;
  label: string;
  /** Indent level for a child option, e.g. a sub-region under its parent. */
  depth?: number;
}

/**
 * Checkbox dropdown for filters that accept several values at once. An empty
 * selection means "everything", matching how the API treats a missing filter.
 */
function MultiSelect({
  labelledBy,
  allLabel,
  noun,
  options,
  selected,
  onChange,
}: {
  labelledBy: string;
  allLabel: string;
  /** Singular noun used in the summary, e.g. "driver" → "3 drivers selected". */
  noun: string;
  options: MultiOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Anything still selected but no longer offered (e.g. a driver with no stops
  // in the current data) stays listed, so it can be unticked.
  const all = useMemo((): MultiOption[] => {
    const known = new Set(options.map((o) => o.value));
    return [...options, ...selected.filter((s) => !known.has(s)).map((s) => ({ value: s, label: s }))];
  }, [options, selected]);

  const labelOf = useMemo(() => new Map(all.map((o) => [o.value, o.label])), [all]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;
  }, [all, query]);

  // Clicking away or pressing Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (labelOf.get(selected[0]) ?? selected[0])
        : `${selected.length} ${noun}s selected`;

  return (
    <div className="difot-ms" ref={rootRef}>
      <button
        type="button"
        className="difot-ms-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelledBy}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected.length ? 'difot-ms-summary strong' : 'difot-ms-summary muted'}>{summary}</span>
        <span aria-hidden="true" className="difot-ms-caret">
          ▾
        </span>
      </button>

      {open && (
        <div className="difot-ms-panel">
          <input
            className="difot-ms-search"
            type="search"
            value={query}
            placeholder={`Search ${noun}s…`}
            aria-label={`Search ${noun}s`}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="difot-ms-actions">
            <button type="button" onClick={() => onChange(visible.map((o) => o.value))} disabled={visible.length === 0}>
              Select {query ? 'shown' : 'all'}
            </button>
            <button type="button" onClick={() => onChange([])} disabled={selected.length === 0}>
              Clear
            </button>
          </div>
          <div className="difot-ms-list" role="listbox" aria-multiselectable="true">
            {visible.length === 0 && <div className="difot-ms-empty">No matches</div>}
            {visible.map((o) => (
              <label
                className="difot-ms-opt"
                key={o.value}
                style={o.depth ? { paddingLeft: `${0.3 + o.depth * 1.1}rem` } : undefined}
              >
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                <span className={o.depth === 0 && all.some((c) => c.depth) ? 'strong' : undefined}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {selected.length > 1 && (
        <div className="difot-ms-chips">
          {selected.map((s) => {
            const label = labelOf.get(s) ?? s;
            return (
              <button
                type="button"
                className="difot-ms-chip"
                key={s}
                title={`Remove ${label}`}
                onClick={() => toggle(s)}
              >
                {label} <span aria-hidden="true">×</span>
                <span className="difot-sr-only">Remove {label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Legend({ items }: { items: { c: string; t: string }[] }) {
  return (
    <div style={{ display: 'flex', gap: '0.85rem', fontSize: 11, color: '#626866' }}>
      {items.map((i) => (
        <span key={i.t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span className="difot-dot" style={{ background: i.c }} />
          {i.t}
        </span>
      ))}
    </div>
  );
}

// ─── KPIs ───────────────────────────────────────────────────────────────────

function Kpis({ report }: { report: DifotReport }) {
  const t = report.totals;
  const cards = [
    {
      label: 'DIFOT',
      value: `${pct(t.completed, t.denom).toFixed(1)}%`,
      sub: `${t.completed.toLocaleString()} of ${t.denom.toLocaleString()} attempted`,
      color: '#13a874',
    },
    {
      label: 'On Time',
      value: `${pct(t.in_window_n, t.completed_with_window).toFixed(1)}%`,
      sub: `of ${t.completed_with_window.toLocaleString()} with a window`,
      color: '#6abd5f',
    },
    {
      label: 'Failed',
      value: t.failed.toLocaleString(),
      sub: `${pct(t.failed, t.denom).toFixed(1)}% failure rate`,
      color: '#d9412f',
    },
    {
      label: 'Pending',
      value: t.pending.toLocaleString(),
      sub: `${pct(t.pending, t.denom).toFixed(1)}% of attempted`,
      color: '#8a7a6b',
    },
    {
      // Cancelled stops sit outside `denom`, so the sub-line is a share of every
      // stop on the plan — quoting it against "attempted" would be wrong.
      label: 'Cancelled',
      value: t.cancelled.toLocaleString(),
      sub: `${pct(t.cancelled, t.total).toFixed(1)}% of ${t.total.toLocaleString()} stops`,
      color: '#626866',
    },
    {
      label: 'Avg Service',
      value: t.avg_service != null ? `${t.avg_service.toFixed(1)}m` : '—',
      sub: `${t.drivers} active drivers`,
      color: '#2c2e2d',
    },
  ];
  return (
    <div className="difot-kpis">
      {cards.map((c) => (
        <div className="difot-kpi" key={c.label}>
          <div className="accent" style={{ background: c.color }} />
          <div className="k-label">{c.label}</div>
          <div className="k-value num" style={{ color: c.color }}>
            {c.value}
          </div>
          <div className="k-sub">{c.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Trend chart ──────────────────────────────────────────────────────────

function TrendChart({ report, period }: { report: DifotReport; period: Period }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  const buckets = useMemo(() => {
    const map = new Map<string, { total: number; failed: number; completed: number; denom: number }>();
    for (const d of report.daily) {
      const k = periodKey(d.d, period);
      const b = map.get(k) ?? { total: 0, failed: 0, completed: 0, denom: 0 };
      b.total += d.total;
      b.failed += d.failed;
      b.completed += d.completed;
      b.denom += d.denom;
      map.set(k, b);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [report, period]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const labels = buckets.map(([k]) => formatPeriodLabel(k, period));
    const difotData = buckets.map(([, b]) => (b.denom ? +pct(b.completed, b.denom).toFixed(1) : null));
    const volData = buckets.map(([, b]) => b.total);
    const failData = buckets.map(([, b]) => b.failed);

    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            type: 'bar',
            label: 'Stops',
            data: volData,
            backgroundColor: 'rgba(98,104,102,0.16)',
            borderColor: 'rgba(98,104,102,0.4)',
            borderWidth: 1,
            yAxisID: 'y2',
            borderRadius: 6,
            order: 3,
          },
          {
            type: 'bar',
            label: 'Failed',
            data: failData,
            backgroundColor: 'rgba(217,65,47,0.75)',
            yAxisID: 'y2',
            borderRadius: 6,
            order: 2,
          },
          {
            type: 'line',
            label: 'DIFOT %',
            data: difotData,
            borderColor: '#13a874',
            backgroundColor: 'rgba(19,168,116,0.12)',
            borderWidth: 3,
            pointRadius: 4,
            pointBackgroundColor: '#13a874',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            fill: true,
            tension: 0.35,
            yAxisID: 'y',
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: { backgroundColor: '#2c2e2d', padding: 10, cornerRadius: 6 },
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: '#626866', font: { size: 11 } } },
          y: {
            position: 'left',
            min: 0,
            max: 100,
            ticks: { callback: (v) => `${v}%`, color: '#626866', font: { size: 11 } },
            grid: { color: '#eef3f0' },
          },
          y2: {
            position: 'right',
            beginAtZero: true,
            ticks: { color: '#626866', font: { size: 11 } },
            grid: { display: false },
          },
        },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [buckets, period]);

  return (
    <div className="difot-chart">
      <canvas ref={canvasRef} />
    </div>
  );
}

// ─── Collected ──────────────────────────────────────────────────────────────

/** Shown as whole pieces; a driver's occasional "1.5 bins" still counts in the sum. */
function formatCount(n: number): string {
  return Math.round(n).toLocaleString();
}

function CollectedPanel({ report, period, flow }: { report: DifotReport; period: Period; flow: Flow }) {
  const measures = COLLECTED_MEASURES[flow];
  const c = report.collected;
  const regions = report.by_region
    .filter((r) => measures.some((m) => r[m.key] > 0))
    .sort((a, b) => b[measures[0].key] - a[measures[0].key]);

  return (
    <section className="card difot-section difot-collected">
      <div className="difot-section-head">
        <div>
          <div className="difot-section-title">Collected</div>
          <div className="difot-section-sub">
            From drivers&rsquo; proof-of-delivery answers · {c.counted_stops.toLocaleString()} of{' '}
            {c.completed_stops.toLocaleString()} completed stops recorded a count
          </div>
        </div>
        <Legend items={measures.map((m) => ({ c: m.color, t: m.label }))} />
      </div>

      <div className="difot-collected-tiles">
        {measures.map((m) => (
          <div className="difot-collected-tile" key={m.key}>
            <div className="lab">{m.label}</div>
            <div className="val num" style={{ color: m.color }}>
              {formatCount(c[m.key])}
            </div>
          </div>
        ))}
        {flow === 'collect' && (
          <div className="difot-collected-tile">
            <div className="lab">Bin volume</div>
            <div className="val num">{(c.bin_litres / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} m³</div>
            <div className="sub">
              Where a bin size was given ({c.sized_stops.toLocaleString()} stops)
            </div>
          </div>
        )}
      </div>

      <CollectedChart report={report} period={period} flow={flow} />

      {regions.length > 0 && (
        <div className="difot-tablewrap" style={{ marginTop: '1rem', maxHeight: 360 }}>
          <table className="difot-table">
            <thead>
              <tr>
                <th>Region</th>
                {measures.map((m) => (
                  <th className="num" key={m.key}>
                    {m.label}
                  </th>
                ))}
                <th className="num">Completed stops</th>
              </tr>
            </thead>
            <tbody>
              {regions.map((r) => (
                <tr key={r.id ?? r.region}>
                  <td>{r.region}</td>
                  {measures.map((m) => (
                    <td className="num" key={m.key} style={{ fontWeight: 600 }}>
                      {formatCount(r[m.key])}
                    </td>
                  ))}
                  <td className="num muted">{r.complete.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CollectedChart({ report, period, flow }: { report: DifotReport; period: Period; flow: Flow }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const measures = COLLECTED_MEASURES[flow];

  const buckets = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    for (const d of report.daily) {
      const k = periodKey(d.d, period);
      const b = map.get(k) ?? {};
      for (const m of measures) b[m.key] = (b[m.key] ?? 0) + d[m.key];
      map.set(k, b);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [report, period, measures]);

  useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: buckets.map(([k]) => formatPeriodLabel(k, period)),
        datasets: measures.map((m, i) => ({
          label: m.label,
          data: buckets.map(([, b]) => b[m.key] ?? 0),
          backgroundColor: m.color,
          stack: 'collected',
          borderRadius: i === measures.length - 1 ? 4 : 0,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { backgroundColor: '#2c2e2d', padding: 10, cornerRadius: 6 },
        },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { color: '#626866', font: { size: 11 } } },
          y: { stacked: true, beginAtZero: true, grid: { color: '#eef3f0' }, ticks: { color: '#626866', font: { size: 11 } } },
        },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [buckets, period, measures]);

  return (
    <div className="difot-chart short">
      <canvas ref={canvasRef} />
    </div>
  );
}

// ─── Region chart + cards ───────────────────────────────────────────────────

function RegionChart({ report }: { report: DifotReport }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const rows = report.by_region;

  useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();
    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: rows.map((r) => r.region),
        datasets: [
          { label: 'Delivered', data: rows.map((r) => r.complete), backgroundColor: '#13a874', stack: 'a', borderRadius: 4 },
          { label: 'Failed', data: rows.map((r) => r.failed), backgroundColor: '#d9412f', stack: 'a' },
          { label: 'Pending', data: rows.map((r) => r.pending), backgroundColor: '#8a7a6b', stack: 'a', borderRadius: 4 },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            position: 'bottom',
            labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 8, padding: 14, color: '#626866', font: { size: 11 } },
          },
          tooltip: {
            backgroundColor: '#2c2e2d',
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              title: (items) => {
                const r = rows[items[0]?.dataIndex ?? 0];
                return r ? (r.parent ? `${r.parent} › ${r.region}` : r.region) : '';
              },
              footer: (items) => {
                const r = rows[items[0]?.dataIndex ?? 0];
                return r ? `DIFOT ${pct(r.complete, r.denom).toFixed(1)}% of ${r.denom.toLocaleString()} attempted` : '';
              },
            },
          },
        },
        scales: {
          x: { stacked: true, ticks: { color: '#626866', font: { size: 11 } }, grid: { color: '#eef3f0' } },
          y: {
            stacked: true,
            ticks: { color: '#2c2e2d', font: { size: 12, weight: 600 }, autoSkip: false },
            grid: { display: false },
          },
        },
      },
    });
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [rows]);

  // One bar per region: grow with the list so 25 Collect operators stay legible.
  return (
    <div className="difot-chart" style={{ height: Math.max(220, rows.length * 30 + 70) }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

/** DIFOT colour bands, matching the TV hero: green from 90%, amber from 75%. */
function difotTone(p: number): string {
  return p >= 90 ? '#13a874' : p >= 75 ? '#f0a020' : '#d9412f';
}

function RegionCards({ report }: { report: DifotReport }) {
  return (
    <div className="difot-state-cards">
      {report.by_region.map((r) => {
        const p = pct(r.complete, r.denom);
        return (
          <div className="difot-state-card" key={r.id ?? r.region}>
            <div className="top">
              <span className="name" title={r.parent ? `${r.parent} › ${r.region}` : r.region}>
                {r.region}
              </span>
              <span className="vol">{r.total.toLocaleString()} stops</span>
            </div>
            <div className="big num">{r.denom ? `${p.toFixed(1)}%` : '—'}</div>
            <div className="difot-bar">
              <div style={{ width: `${Math.min(p, 100)}%`, background: difotTone(p) }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Failure panel ────────────────────────────────────────────────────────

function FailurePanel({ failures }: { failures: DifotStop[] | null }) {
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of failures ?? []) {
      const key = categoriseFailure(f.fail_note);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return FAIL_CATEGORIES.map((c) => ({ ...c, count: counts.get(c.key) ?? 0 })).filter((c) => c.count > 0);
  }, [failures]);

  const total = failures?.length ?? 0;

  return (
    <section className="card difot-section">
      <div className="difot-section-head">
        <div>
          <div className="difot-section-title">Failure Reasons</div>
          <div className="difot-section-sub">From driver attempt notes</div>
        </div>
        <span className="difot-pill" style={{ background: '#fdecea', color: '#d9412f' }}>
          <span className="difot-dot" style={{ background: '#d9412f' }} />
          {total}
        </span>
      </div>

      {categories.length > 0 && (
        <div className="difot-cat" style={{ marginBottom: '1rem' }}>
          {categories
            .sort((a, b) => b.count - a.count)
            .map((c) => {
              const p = total ? (c.count / total) * 100 : 0;
              return (
                <div className="difot-cat-row" key={c.key}>
                  <div>
                    <div className="lab">{c.label}</div>
                    <div className="difot-bar">
                      <div style={{ width: `${p}%`, background: c.color }} />
                    </div>
                  </div>
                  <div className="cnt" style={{ color: c.color }}>
                    {c.count}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <div className="difot-scroll">
        {failures && failures.length === 0 && (
          <div className="difot-empty">No failed deliveries for the selected filters</div>
        )}
        {(failures ?? []).slice(0, 100).map((f) => (
          <div className="difot-fail" key={f.stop_id}>
            <div className="row1">
              <span className="so">{f.so || f.ref || `Stop ${f.stop_id}`}</span>
              {f.region && (
                <span className="difot-pill" style={{ background: '#e9f7f1', color: '#0a5c40' }}>
                  {f.region}
                </span>
              )}
            </div>
            <div className="meta">
              {(f.driver || 'Unassigned') + ' · ' + formatDateAU(f.trip_date)}
            </div>
            <div className="note">“{(f.fail_note || 'No reason recorded').slice(0, 200)}”</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Driver leaderboard ─────────────────────────────────────────────────────

function DriverTable({ report }: { report: DifotReport }) {
  return (
    <div className="difot-tablewrap">
      <table className="difot-table">
        <thead>
          <tr>
            <th className="num">#</th>
            <th>Driver / Run</th>
            <th>Region</th>
            <th className="num">Attempted</th>
            <th className="num">DIFOT %</th>
            <th className="num">On Time %</th>
            <th className="num">Failed</th>
            <th className="num">Avg Service</th>
          </tr>
        </thead>
        <tbody>
          {report.drivers.map((d, i) => (
            <tr key={d.driver}>
              <td className="num difot-rank">{i + 1}</td>
              <td>{d.driver}</td>
              <td className="muted">{d.region ?? '—'}</td>
              <td className="num">{d.denom.toLocaleString()}</td>
              <td className="num" style={{ fontWeight: 700, color: '#2c2e2d' }}>
                {pct(d.completed, d.denom).toFixed(1)}%
              </td>
              <td className="num">{d.with_window ? `${pct(d.in_window_n, d.with_window).toFixed(1)}%` : '—'}</td>
              <td className="num" style={{ color: d.failed ? '#d9412f' : undefined }}>
                {d.failed}
              </td>
              <td className="num">{d.avg_service != null ? `${d.avg_service.toFixed(1)}m` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Stop lookup (search by sales order / tracking reference) ───────────────

function StopLookup() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DifotStop[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(e: FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setBusy(true);
    try {
      setResults(await fetchStops({ q: term, limit: 25 }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card difot-section" style={{ marginTop: '1.25rem' }}>
      <div className="difot-section-head">
        <div>
          <div className="difot-section-title">Stop Lookup</div>
          <div className="difot-section-sub">Search by sales order (SO…) or tracking reference</div>
        </div>
      </div>
      <form onSubmit={run} className="difot-search" style={{ display: 'flex', gap: '0.5rem', maxWidth: 420 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. SO3978005"
          aria-label="Search sales order or reference"
        />
        <button type="submit" className="difot-btn" disabled={busy}>
          {busy ? '…' : 'Search'}
        </button>
      </form>

      {results && (
        <div className="difot-tablewrap" style={{ marginTop: '1rem' }}>
          {results.length === 0 ? (
            <div className="difot-empty">No matching stops</div>
          ) : (
            <table className="difot-table">
              <thead>
                <tr>
                  <th>Sales Order</th>
                  <th>Status</th>
                  <th>Driver / Run</th>
                  <th>Region</th>
                  <th>Date</th>
                  <th>Window</th>
                  <th>On time</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.stop_id}>
                    <td style={{ fontWeight: 600 }}>{r.so || r.ref}</td>
                    <td>{r.status}</td>
                    <td>{r.driver ?? '—'}</td>
                    <td>{r.region ?? '—'}</td>
                    <td>{formatDateAU(r.trip_date)}</td>
                    <td className="muted">{r.w_start ? `${r.w_start}–${r.w_end}` : '—'}</td>
                    <td>{r.in_window == null ? '—' : r.in_window ? '✓' : '✗'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
